import { formatInTimeZone } from 'date-fns-tz'
import {
  getSiteDateFormat,
  getTimePattern,
  type DateFormatStyle,
} from '@hapana/shared/web/utils/date-format-utils'

export const DATE_FORMAT_KEY_TO_STYLE: { [K in DateFormatKey]?: DateFormatStyle } = {
  SHORT_DATE: 'medium',
  FULL_DATE: 'full',
  DATE_ONLY: 'medium',
  DATE_WITH_TIME: 'mediumWithTime',
}

export const DATE_FORMATS = {
  SHORT_DATE: 'MMM d, yyyy',
  FULL_DATE: 'EEEE, d MMM yyyy',
  DATE_ONLY: 'd MMM yyyy',
  ISO_DATETIME: "yyyy-MM-dd'T'HH:mm:ss",
} as const

const DYNAMIC_FORMAT_KEYS = {
  TIME_ONLY: 'TIME_ONLY',
  DATE_WITH_TIME: 'DATE_WITH_TIME',
} as const

export type DateFormatKey = keyof typeof DATE_FORMATS | keyof typeof DYNAMIC_FORMAT_KEYS

const resolveDateFormat = (format: DateFormatKey | string): string => {
  const style = DATE_FORMAT_KEY_TO_STYLE[format as keyof typeof DATE_FORMAT_KEY_TO_STYLE]
  if (style) {
    return getSiteDateFormat(style)
  }
  if (format === DYNAMIC_FORMAT_KEYS.TIME_ONLY) {
    return getTimePattern()
  }
  if (format === DYNAMIC_FORMAT_KEYS.DATE_WITH_TIME) {
    return `d MMM yyyy, ${getTimePattern()}`
  }
  return DATE_FORMATS[format as keyof typeof DATE_FORMATS] ?? format
}

export const formatDateWithTimezone = (
  date: Date | number | string,
  timezone = 'UTC',
  format: DateFormatKey | string = 'SHORT_DATE'
): string => {
  const dateObj = date instanceof Date ? date : new Date(date)

  if (Number.isNaN(dateObj.getTime())) {
    return '-'
  }

  const formatPattern = resolveDateFormat(format)
  const tz = timezone || 'UTC'

  try {
    return formatInTimeZone(dateObj, tz, formatPattern)
  } catch {
    return formatInTimeZone(dateObj, 'UTC', formatPattern)
  }
}

/**
 * Days of the week starting from Sunday (index 0), matching JavaScript's Date.getDay()
 */
export const DAYS_OF_WEEK = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const

export type DayOfWeek = (typeof DAYS_OF_WEEK)[number]

/**
 * Get the day name for a given date
 * @param date - Date object
 * @returns lowercase day name (e.g., 'monday', 'tuesday')
 */
export const getDayOfWeek = (date: Date): DayOfWeek => {
  // Date.getDay() always returns 0-6, so this is safe
  return DAYS_OF_WEEK[date.getDay()] as DayOfWeek
}

/**
 * Convert 12-hour time string to 24-hour format
 * @param {string} time12h - Time in "9:00 AM" or "9:00 PM" format
 * @returns {string|null} - Time in "09:00" format or null
 */
export const convertTo24Hour = (time12h: string): string | null => {
  if (!time12h || typeof time12h !== 'string') {
    return null
  }

  const match = time12h.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!match) {
    return null
  }

  const [, hourStr, minuteStr, period] = match
  const safeHour = hourStr
  const safeMinute = minuteStr
  const safePeriod = period

  let hour = parseInt(safeHour ?? '0', 10)
  const minute = parseInt(safeMinute ?? '0', 10)

  // Convert to 24-hour format
  if (safePeriod?.toLowerCase() === 'am' && hour === 12) {
    hour = 0
  } else if (safePeriod?.toLowerCase() === 'pm' && hour !== 12) {
    hour += 12
  }

  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
}

export const timeTwentyFourHoursAgo = (): number => {
  const now = Date.now()
  return now - 24 * 60 * 60 * 1000
}

export const asDate = (value?: string | number | Date | null): Date | null => {
  if (value === null || value === undefined) {
    return null
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    // Handle numeric timestamp strings (e.g., "1234567890" or "1234567890000")
    if (/^\d+$/.test(trimmed)) {
      const numeric = Number(trimmed)
      // If 10 digits or less, assume seconds; otherwise milliseconds
      const millis = trimmed.length <= 10 ? numeric * 1000 : numeric
      const parsed = new Date(millis)
      return Number.isNaN(parsed.getTime()) ? null : parsed
    }
    const parsed = new Date(trimmed)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  if (typeof value === 'number') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  return null
}

/**
 * Convert 24-hour time string to 12-hour format
 * @param {string} time24h - Time in "09:00" format
 * @returns {string|null} - Time in "9:00 AM" format or null
 */
export const convertTo12Hour = (time24h: string): string | null => {
  if (!time24h || typeof time24h !== 'string') {
    return null
  }

  const match = time24h.match(/^(\d{2}):(\d{2})$/)
  if (!match) {
    return null
  }

  const [, hourStr, minuteStr] = match
  let hour = parseInt(hourStr ?? '0', 10)

  const period = hour >= 12 ? 'PM' : 'AM'

  // Convert to 12-hour format
  if (hour === 0) {
    hour = 12
  } else if (hour > 12) {
    hour -= 12
  }

  return `${hour}:${minuteStr} ${period}`
}

/**
 * Convert Date object to 24-hour format
 * @param {Date} dateObj - Date object from TimePicker
 * @returns {string|null} - Time in "09:00" format or null
 */
export const convertDateTo24Hour = (dateObj?: Date | null): string | null => {
  if (!dateObj || !(dateObj instanceof Date)) {
    return null
  }

  const hours = dateObj.getHours().toString().padStart(2, '0')
  const minutes = dateObj.getMinutes().toString().padStart(2, '0')

  return `${hours}:${minutes}`
}

/**
 * Validate time format (HH:MM)
 * @param {string} timeStr - Time string to validate
 * @returns {boolean} - True if valid 24-hour format
 */
export const isValidTimeFormat = (timeStr: string): boolean => {
  if (!timeStr || typeof timeStr !== 'string') {
    return false
  }
  return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeStr)
}
